import { useEffect, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { useParams, matchPath, useLocation, useNavigate } from 'react-router-dom';
import DialogBox from '@/components/DialogBox';
import { PageLink } from '@/components/TopPageLinks/TopPageLinks.component';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import { supabase } from '@/lib/supabase';
import candidatesServices, { DeleteCandidateParams } from '@/services/candidates';
import staffMemberServices from '@/services/staffMember';
import { chakraExtendedTheme } from '@/styles/theme';
import Modal from '@/trio-ui/Modal';
import { FetchApiIds } from '@/types';
import { Paths } from '@/types/routes';
import { getMostRecentStaffMemberActiveJob } from '@/utils/staffMember';
import StaffMemberInfoComponent from './StaffMemberInfo.component';
import Context from './StaffMemberInfo.context';

const StaffMemberInfo = (): JSX.Element => {
  const { id = '' } = useParams();
  const location = useLocation();
  const [directLeaderId, setDirectLeaderId] = useState<string>('');
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const toast = useToastContext();

  const { data: staffMember, refetch: refreshStaffMember } = useApiFetch(
    [FetchApiIds.GET_STAFF_MEMBER, id],
    async () => staffMemberServices.getStaffMember({ staffMemberId: id })
  );

  const { data: directLeader } = useApiFetch(
    [FetchApiIds.GET_STAFF_MEMBER, directLeaderId],
    async () => staffMemberServices.getStaffMember({ staffMemberId: directLeaderId }),
    { enabled: !!directLeaderId }
  );

  useEffect(() => {
    const currentStaffMemberJob = staffMember?.jobInfo
      ? getMostRecentStaffMemberActiveJob(staffMember.jobInfo)
      : undefined;

    if (currentStaffMemberJob?.directLeaderId) {
      setDirectLeaderId(currentStaffMemberJob.directLeaderId);
    }
  }, [staffMember]);

  const topPageLinks: PageLink[] = [
    {
      label: 'Personal',
      to: { path: Paths.STAFF_MEMBER_INFO, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_INFO, location.pathname),
    },
    {
      label: 'Job',
      to: { path: Paths.STAFF_MEMBER_JOB, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_JOB, location.pathname),
    },
    // {
    //   label: 'Reviews',
    //   to: { path: Paths.STAFF_MEMBER_REVIEWS, params: { id } },
    //   isActived: !!matchPath(Paths.STAFF_MEMBER_REVIEWS, location.pathname),
    //   isDisabled: true,
    // },
    {
      label: 'PTO',
      to: { path: Paths.STAFF_MEMBER_TIME_OFF, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_TIME_OFF, location.pathname),
    },
    {
      label: 'Notes',
      to: { path: Paths.STAFF_MEMBER_NOTES, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_NOTES, location.pathname),
    },
    {
      label: 'Professional Background',
      to: { path: Paths.STAFF_MEMBER_BACKGROUND, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_BACKGROUND, location.pathname),
    },
    {
      label: 'Attachments',
      to: { path: Paths.STAFF_MEMBER_ATTACHMENTS, params: { id } },
      isActived: !!matchPath(Paths.STAFF_MEMBER_ATTACHMENTS, location.pathname),
    },
  ];

  const refreshUser = (): void => {
    refreshStaffMember();
  };

  const [openInactiveModal, setOpenInactiveModal] = useState(false);
  const [inactiveDate, setInactiveDate] = useState(new Date().toISOString().split('T')[0]);

  const deleteCandidateMutation = useApiSubmit<string, DeleteCandidateParams>(
    candidatesServices.deleteCandidate
  );

  const onDeleteCandidate = async (): Promise<void> => {
    if (!staffMember) return;

    try {
      await deleteCandidateMutation.mutateAsync({ id: staffMember.person.id });
      setOpenDeleteModal(false);
      navigate(Paths.CANDIDATES);
      toast.showSuccessFeedback('The staff member has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the staff member.');
    }
  };

  const onConfirmInactive = async (): Promise<void> => {
    if (!staffMember) return;
    try {
      await supabase
        .from('profiles')
        .update({ employee_work_status: 'inactive' })
        .eq('id', staffMember.person.id);

      await supabase
        .from('team_members')
        .update({ end_date: inactiveDate })
        .eq('profile_id', staffMember.person.id)
        .is('end_date', null);

      setOpenInactiveModal(false);
      refreshStaffMember();
      toast.showSuccessFeedback(`${staffMember.person.firstName} set as inactive.`);
    } catch {
      toast.showErrorFeedback('Failed to set member as inactive.');
    }
  };

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <Context.Provider value={{ refreshUser, staffMember, directLeader }}>
        <StaffMemberInfoComponent
          topPageLinks={topPageLinks}
          staffMember={staffMember}
          onDeleteClick={() => setOpenDeleteModal(true)}
          onSetInactiveClick={() => {
            setInactiveDate(new Date().toISOString().split('T')[0]);
            setOpenInactiveModal(true);
          }}
          onSetActiveClick={async () => {
            if (!staffMember) return;
            try {
              await supabase
                .from('profiles')
                .update({ employee_work_status: 'active' })
                .eq('id', staffMember.person.id);
              refreshStaffMember();
              toast.showSuccessFeedback(`${staffMember.person.firstName} set as active.`);
            } catch {
              toast.showErrorFeedback('Failed to set member as active.');
            }
          }}
        />
        <DialogBox
          data-testid="delete-candidate-dialog"
          isOpen={openDeleteModal}
          onCancelHandler={() => setOpenDeleteModal(false)}
          onConfirmHandler={onDeleteCandidate}
        />
        <Modal
          isCentered
          size="sm"
          isOpen={openInactiveModal}
          onClose={() => setOpenInactiveModal(false)}
          modalTitle={`Set as Inactive — ${staffMember?.person?.firstName ?? ''} ${
            staffMember?.person?.lastName ?? ''
          }`}
          onConfirmLabel="Confirm"
          onConfirmClick={onConfirmInactive}
          bodyRender={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="inactive-date"
                style={{ fontSize: 14, fontWeight: 500, color: '#09090B' }}
              >
                Inactive Date
              </label>
              <input
                id="inactive-date"
                type="date"
                value={inactiveDate}
                onChange={(e) => setInactiveDate(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #E4E4E7',
                  fontSize: 14,
                  width: '100%',
                }}
              />
              <p style={{ fontSize: 12, color: '#71717A', margin: 0 }}>
                When did this person stop working? This will also set their end date on all active
                teams.
              </p>
            </div>
          }
        />
      </Context.Provider>
    </ChakraProvider>
  );
};

export default StaffMemberInfo;
